import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Perguntas do check do canal (Adnéia, 20/10/2026). As perguntas e as datas de corte
// vêm do backend (data/checklists_canal.py, GET /api/dashboard/canal-checklists).
// Decisões: https://claude.ai/artifact/KUGxsTFFU3qnVjYECwMrdz

const hora = (iso) => {
  try { return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }); }
  catch { return '?'; }
};

// Texto do title do ✓ (passar o mouse): quem, hora, números do portal e cada resposta.
export const resumoDoCheck = (ck, lista) => {
  if (!ck) return '';
  const linhas = [`${ck.situacao === 'pendencia' ? 'Feito com pendência' : 'Feito'}: ${ck.marcado_por || '?'} às ${hora(ck.marcado_em)}`];
  const n = ck.numeros || {};
  if (lista?.numeros?.campos?.length && Object.keys(n).length) {
    linhas.push('Portal: ' + lista.numeros.campos.map(([k, r]) => `${r} ${n[k] ?? '?'}`).join(' · '));
  }
  (ck.respostas || []).forEach(r => linhas.push(`${r.ruim ? '✗' : (r.avisa ? '🔔' : '•')} ${r.status}: ${r.rotulo}`));
  if (ck.o_que_ficou) linhas.push(`${ck.situacao === 'pendencia' ? 'Ficou' : 'Para a Adnéia'}: ${ck.o_que_ficou}`);
  return linhas.join('\n');
};

// Uma linha da relação de retorno: de que etapa para qual, e contra o quê se comparou.
const detalheRetorno = (i, inicio) => {
  if (i.origem === 'retorno') return `${i.etapa_antes} → ${i.etapa} em ${i.desde} · último retorno ${i.base_em}${i.base_por ? ` (${i.base_por})` : ''}`;
  if (i.origem === 'abertura') return `${i.etapa_antes} → ${i.etapa} em ${i.desde} · aberto em ${i.base_em}`;
  return `andou em ${i.desde} (atendimento de antes de ${inicio})`;
};

// Janela que abre no ✓ do canal com perguntas. `atual` = o check de hoje (se já marcado):
// a janela vem preenchida para responder de novo ou desmarcar.
// Canal com `retorno` (Sicredi, 22/10/2026): a janela mostra a relação dos pedidos que mudaram
// de etapa (GET /api/dashboard/retorno-canal), com o "Dei o retorno" em cada linha, e a pergunta
// automática é respondida pelo ELO pela quantidade de linhas. Decisões:
// https://claude.ai/artifact/PTqMAg9atqSPjXn2C4VRBg
const CheckCanalDialog = ({ canal, lista, atual, onClose, onSalvo }) => {
  const { getAuthHeader } = useAuth();
  const [resp, setResp] = useState({});
  const [nums, setNums] = useState({});
  const [ficou, setFicou] = useState('');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [rel, setRel] = useState(null);        // relação de retorno: null = carregando
  const [relErro, setRelErro] = useState('');
  const [dando, setDando] = useState('');      // id do atendimento gravando o "Dei o retorno"

  const canalRetorno = lista?.retorno || '';

  const carregarRel = useCallback(() => {
    if (!canalRetorno) return;
    setRel(null); setRelErro('');
    axios.get(`${API_URL}/api/dashboard/retorno-canal`, { params: { canal: canalRetorno }, headers: getAuthHeader() })
      .then(r => setRel(r.data))
      .catch(() => setRelErro('A relação não carregou.'));
  }, [canalRetorno, getAuthHeader]);

  useEffect(() => {
    if (!canal) return;
    const r = {};
    (atual?.respostas || []).forEach(x => { r[x.id] = x.resposta; });
    setResp(r);
    const n = {};
    Object.entries(atual?.numeros || {}).forEach(([k, v]) => { n[k] = String(v); });
    setNums(n);
    setFicou(atual?.o_que_ficou || '');
    setErro('');
  }, [canal, atual]);

  useEffect(() => { if (canal) carregarRel(); }, [canal, carregarRel]);

  if (!canal || !lista) return null;

  // Atividade (ex.: "atividade:canc_aes") aparece pelo nome da tela; ela pode não ter número.
  const nome = lista.rotulo || canal;
  const ehAtividade = canal.startsWith('atividade:');
  const campos = lista.numeros?.campos || [];
  const semRetorno = rel?.itens?.length || 0;
  const temRuim = lista.perguntas.some(p => (p.auto
    ? semRetorno > 0
    : p.opcoes.some(o => o.ruim && o.valor === resp[p.id])));
  // Resposta com 🔔 (Tem em Casa, 23/10/2026): fica verde, mas pede a linha do que foi conferido
  // e avisa a Adnéia no sininho.
  const temAviso = lista.perguntas.some(p => !p.auto && p.opcoes.some(o => o.avisa && o.valor === resp[p.id]));
  const pedeLinha = temRuim || temAviso;

  const darRetorno = async (item) => {
    setDando(item.id);
    try {
      const r = await axios.post(`${API_URL}/api/chamados/${item.id}/retorno-canal`, { canal: canalRetorno }, { headers: getAuthHeader() });
      setRel(prev => prev && { ...prev, itens: prev.itens.filter(x => x.id !== item.id) });
      if (r.data.ja_estava) toast.info(`${item.entrega}: o retorno já estava registrado${r.data.por ? ` por ${r.data.por}` : ''}.`);
      else toast.success(`${item.entrega}: retorno registrado no atendimento.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Não gravou. Tente de novo.');
      if (e?.response?.status === 409) carregarRel();
    } finally {
      setDando('');
    }
  };

  const enviar = async (corpo) => {
    setSalvando(true); setErro('');
    try {
      const res = await axios.post(`${API_URL}/api/dashboard/canal-checks`, { canal, ...corpo }, { headers: getAuthHeader() });
      onSalvo(res.data);
      if (res.data.checked && res.data.check?.situacao === 'pendencia') {
        toast.warning(`${nome}: feito com pendência. A Adnéia recebe o aviso.`);
      } else if (res.data.checked && res.data.check?.avisos?.length) {
        toast.success(`${nome}: check feito. A Adnéia recebe o aviso.`);
      } else if (res.data.checked) {
        toast.success(`${nome}: check feito`);
      } else {
        toast.success(`${nome}: check desmarcado. As respostas ficaram no histórico.`);
      }
      onClose();
    } catch (e) {
      setErro(e?.response?.data?.detail || 'Não gravou. Tente de novo.');
      // A relação pode ter mudado (outra pessoa deu retorno ou o pedido andou): lê de novo.
      if (canalRetorno) carregarRel();
    } finally {
      setSalvando(false);
    }
  };

  const confirmar = () => {
    if (canalRetorno && !rel) { setErro(relErro ? 'A relação de retorno não carregou. Clique em "Tentar de novo".' : 'Espere a relação de retorno carregar.'); return; }
    const faltaNum = campos.find(([k]) => !/^\d+$/.test((nums[k] || '').trim()));
    if (faltaNum) { setErro(`Preencha quantos tickets estão em ${faltaNum[1]} (0 se não houver nenhum).`); return; }
    const faltaResp = lista.perguntas.find(p => !p.auto && !resp[p.id]);
    if (faltaResp) { setErro(`Responda a pergunta: ${faltaResp.status}.`); return; }
    if (pedeLinha && !ficou.trim()) {
      setErro(temRuim ? 'Escreva em uma linha o que ficou e por quê.' : 'Escreva em uma linha o que você conferiu, para a Adnéia.');
      return;
    }
    const numeros = {};
    campos.forEach(([k]) => { numeros[k] = parseInt(nums[k], 10); });
    enviar({ acao: 'marcar', respostas: resp, numeros, o_que_ficou: pedeLinha ? ficou.trim() : '' });
  };

  return (
    <Dialog open={!!canal} onOpenChange={(o) => { if (!o && !salvando) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{nome} · check de {lista.dia_semana}, {lista.hoje_br}</DialogTitle>
          <DialogDescription>Responda depois de passar {ehAtividade ? 'pela tela' : 'pelo portal'}. Cada resposta fica gravada com o seu nome e a hora.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-md bg-muted/60 px-3 py-2 text-sm">
          {lista.datas.map(d => (
            <React.Fragment key={d.data + d.texto}>
              <span className="font-mono font-bold text-emerald-700 dark:text-emerald-300 whitespace-nowrap">antes de {d.data}</span>
              <span>{d.texto}</span>
            </React.Fragment>
          ))}
        </div>

        {campos.length > 0 && (
        <div className="space-y-2 border-t pt-3">
          <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Retrato do portal agora</p>
          <p className="text-sm font-semibold">{lista.numeros.pergunta}</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {campos.map(([k, rotulo]) => (
              <label key={k} className="flex flex-col gap-1 text-xs text-muted-foreground">
                {rotulo}
                <input
                  type="number" min="0" inputMode="numeric" value={nums[k] ?? ''}
                  onChange={(e) => { setNums(prev => ({ ...prev, [k]: e.target.value })); setErro(''); }}
                  className="h-9 rounded-md border bg-background px-2 text-sm text-foreground"
                />
              </label>
            ))}
          </div>
        </div>
        )}

        {canalRetorno && (
          <div className="space-y-2 border-t pt-3">
            <p className="text-[11px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">
              {rel?.titulo || 'Andou: avisar o canal'}{rel ? ` (${semRetorno})` : ''}
            </p>
            <p className="text-xs text-muted-foreground">Pedidos que mudaram de etapa desde o último retorno. Dê o retorno por e-mail e clique em "Dei o retorno": a anotação vai para o atendimento.</p>
            {!rel && !relErro && <p className="text-sm text-muted-foreground flex items-center gap-2"><RefreshCw className="h-4 w-4 animate-spin" /> Conferindo…</p>}
            {relErro && (
              <p className="text-sm text-red-600 flex items-center gap-2">{relErro}
                <Button type="button" size="sm" variant="outline" onClick={carregarRel}>Tentar de novo</Button>
              </p>
            )}
            {rel && semRetorno === 0 && <p className="text-sm text-emerald-700 dark:text-emerald-300">Nenhum pedido para avisar.</p>}
            {rel && rel.itens.map(item => (
              <div key={item.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm">
                <div className="min-w-0">
                  <a href={`/chamados/editar/${item.id}`} target="_blank" rel="noreferrer"
                     title={`Abrir o atendimento ${item.id_atendimento || ''} em outra aba`}
                     className="font-mono font-semibold text-primary underline underline-offset-2">{item.entrega}</a>
                  <span> · {item.status}</span>
                  <p className="text-xs text-muted-foreground">{detalheRetorno(item, rel.inicio)}</p>
                </div>
                <Button type="button" size="sm" disabled={!!dando} onClick={() => darRetorno(item)}>
                  {dando === item.id ? 'Gravando…' : 'Dei o retorno'}
                </Button>
              </div>
            ))}
          </div>
        )}

        {lista.perguntas.map((p, i) => (
          <div key={p.id} className="space-y-2 border-t pt-3">
            <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{i + 1} · {p.status}</p>
            <p className="text-sm font-semibold">{p.texto}</p>
            {p.auto ? (
              <p className={`rounded-md px-3 py-2 text-sm ${!rel
                ? 'bg-muted text-muted-foreground'
                : (semRetorno > 0
                    ? 'bg-amber-50 font-semibold text-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
                    : 'bg-emerald-50 font-semibold text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300')}`}>
                {!rel ? (relErro ? 'Não deu para conferir agora.' : 'Conferindo…')
                  : (semRetorno > 0
                      ? `${semRetorno} ${semRetorno === 1 ? 'pedido' : 'pedidos'} sem retorno: se der o check assim, ele fica amarelo.`
                      : 'Nenhum pedido sem retorno.')}
              </p>
            ) : (
            <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={p.status}>
              {p.opcoes.map(o => {
                const sel = resp[p.id] === o.valor;
                return (
                  <button
                    key={o.valor} type="button" role="radio" aria-checked={sel}
                    onClick={() => { setResp(prev => ({ ...prev, [p.id]: o.valor })); setErro(''); }}
                    title={o.avisa ? 'O check fica verde e a Adnéia recebe o aviso no sininho' : undefined}
                    className={`min-h-[36px] rounded-md border px-3 text-sm transition-colors ${sel
                      ? (o.ruim
                          ? 'border-amber-500 bg-amber-50 font-semibold text-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
                          : 'border-emerald-600 bg-emerald-50 font-semibold text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300')
                      : 'hover:bg-muted'}`}
                  >{o.avisa ? '🔔 ' : ''}{o.rotulo}</button>
                );
              })}
            </div>
            )}
          </div>
        ))}

        {pedeLinha && (
          <label className="flex flex-col gap-1 border-t pt-3 text-sm">
            {temRuim
              ? <span className="font-semibold text-amber-800 dark:text-amber-300">O que ficou e por quê (o check fica amarelo e a Adnéia recebe o aviso)</span>
              : <span className="font-semibold text-sky-800 dark:text-sky-300">🔔 O que você conferiu: o pedido e o que encontrou (o check fica verde e a Adnéia recebe o aviso)</span>}
            <textarea
              rows={2} maxLength={500} value={ficou}
              onChange={(e) => { setFicou(e.target.value); setErro(''); }}
              className="rounded-md border bg-background px-2 py-1.5 text-sm"
            />
          </label>
        )}

        {erro && <p className="text-sm font-semibold text-red-600">{erro}</p>}

        <DialogFooter className="flex flex-wrap gap-2 sm:justify-between">
          <div>
            {atual && (
              <Button type="button" variant="outline" disabled={salvando} onClick={() => enviar({ acao: 'desmarcar' })}
                className="text-red-700 dark:text-red-400">Desmarcar</Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" disabled={salvando} onClick={onClose}>Cancelar</Button>
            <Button type="button" disabled={salvando} onClick={confirmar}>
              {salvando ? 'Gravando…' : 'Confirmar check'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CheckCanalDialog;

const ACAO_COR = {
  'marcou': 'text-emerald-700 dark:text-emerald-300',
  'marcou com pendência': 'text-amber-700 dark:text-amber-300',
  'desmarcou': 'text-red-600',
};

// Botão "Respostas" (só a gestão): um dia por linha nos últimos 30 dias; clicar abre as respostas.
export const RespostasCanal = ({ canal, lista, onClose }) => {
  const { getAuthHeader } = useAuth();
  const [linhas, setLinhas] = useState(null);
  const [erro, setErro] = useState('');
  const [aberta, setAberta] = useState('');

  useEffect(() => {
    if (!canal) return;
    setLinhas(null); setErro(''); setAberta('');
    axios.get(`${API_URL}/api/dashboard/canal-checks/historico`, { params: { canal, dias: 30 }, headers: getAuthHeader() })
      .then(r => setLinhas(r.data.linhas || []))
      .catch(() => setErro('Não carregou as respostas. Tente de novo.'));
  }, [canal, getAuthHeader]);

  const campos = lista?.numeros?.campos || [];
  const dia = (s) => `${s.slice(8, 10)}/${s.slice(5, 7)}`;

  return (
    <Dialog open={!!canal} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Respostas do check: {lista?.rotulo || canal}</DialogTitle>
          <DialogDescription>Últimos 30 dias, um dia por linha. Clique no dia para ver as respostas e quem marcou ou desmarcou. As perguntas começam no dia em que esta versão do ELO entrou no ar.</DialogDescription>
        </DialogHeader>
        {erro && <p className="text-sm text-red-600">{erro}</p>}
        {!erro && linhas === null && <p className="text-sm text-muted-foreground flex items-center gap-2"><RefreshCw className="h-4 w-4 animate-spin" /> Carregando…</p>}
        {linhas && linhas.length === 0 && <p className="text-sm text-muted-foreground">Nada registrado ainda.</p>}
        {linhas && linhas.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-1.5 pr-2">Dia</th><th className="py-1.5 pr-2">Quem</th><th className="py-1.5 pr-2">Check</th>
                  {campos.map(([k, r]) => <th key={k} className="py-1.5 px-1 text-center">{r}</th>)}
                  <th className="py-1.5 pl-2">Ficou / 🔔 aviso</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map(l => {
                  const ck = l.check;
                  const n = ck?.numeros || {};
                  const sit = !ck ? 'sem check' : (ck.situacao === 'pendencia' ? 'amarelo' : (ck.respostas?.length ? 'verde' : 'verde, sem perguntas'));
                  const cor = !ck ? 'text-muted-foreground' : (ck.situacao === 'pendencia' ? 'text-amber-700 dark:text-amber-300 font-semibold' : 'text-emerald-700 dark:text-emerald-300 font-semibold');
                  return (
                    <React.Fragment key={l.data}>
                      <tr className="border-b cursor-pointer hover:bg-muted/50" onClick={() => setAberta(aberta === l.data ? '' : l.data)}>
                        <td className="py-1.5 pr-2 whitespace-nowrap font-medium">{dia(l.data)}</td>
                        <td className="py-1.5 pr-2">{ck?.marcado_por || '—'}</td>
                        <td className={`py-1.5 pr-2 whitespace-nowrap ${cor}`}>{sit}</td>
                        {campos.map(([k]) => <td key={k} className="py-1.5 px-1 text-center tabular-nums">{n[k] ?? '?'}</td>)}
                        <td className="py-1.5 pl-2 text-xs">{ck?.o_que_ficou ? `${ck.situacao !== 'pendencia' ? '🔔 ' : ''}${ck.o_que_ficou}` : '—'}</td>
                      </tr>
                      {aberta === l.data && (
                        <tr className="border-b bg-muted/30">
                          <td colSpan={4 + campos.length} className="px-2 py-2 text-xs space-y-1">
                            {(ck?.respostas || []).map(r => (
                              <div key={r.id}><span className={r.ruim ? 'text-amber-700 dark:text-amber-300 font-semibold' : (r.avisa ? 'text-sky-700 dark:text-sky-300 font-semibold' : '')}>{r.ruim ? '✗' : (r.avisa ? '🔔' : '•')} {r.pergunta}</span> <b>{r.rotulo}</b></div>
                            ))}
                            {(l.eventos || []).map((e, i) => (
                              <div key={i} className="text-muted-foreground">{hora(e.em)} · {e.por || '?'} <span className={ACAO_COR[e.acao] || ''}>{e.acao}</span>{e.o_que_ficou ? ` · ${e.situacao === 'pendencia' ? 'ficou' : 'para a Adnéia'}: ${e.o_que_ficou}` : ''}</div>
                            ))}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
